import React, { useEffect, useState } from 'react'
import {
    FormControl,
    Grid,
    ListSubheader,
    MenuItem,
    Select,
    Stack,
    Typography,
    Card,
    Box,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faTimes } from '@fortawesome/free-solid-svg-icons'

import QueryBuilderField from './QueryBuilderField'
import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import QueryBuilderLink from './QueryBuilderLink'

import {
    QueryBuilderCombinatorProps,
    DataField,
    LinkField,
    CombinatorField,
} from './types'
import { CompactButton } from '@/Components/Elements/StyledComponents/Buttons'
import { brandedTokens } from '@/theme/tokens'
import { listSubheaderProps } from '@/Components/Elements/StyledComponents/styles'

const QueryBuilderCombinator: React.FC<QueryBuilderCombinatorProps> = ({
    field,
    index,
    level,
    defdossier,
    defdossierlink,
    ddObject,
    linkField,
    kedo,
    onChange,
    onDelete,
}) => {
    const [uuid] = useState<string>(field?.uuid || kedo.utils().uuidv4())
    const [combine, setCombine] = useState<'AND' | 'OR'>(
        field?.combine || 'AND'
    )
    const [fields, setFields] = useState<DataField[]>(() => {
        const initialFields = field?.fields?.map((f) => ({ ...f })) || []
        return initialFields.map((f) => ({
            ...f,
            uuid: f.uuid || kedo.utils().uuidv4(),
        }))
    })
    const [links, setLinks] = useState<LinkField[]>(() => {
        const initialLinks = field?.links?.map((l) => ({ ...l })) || []
        return initialLinks.map((l) => ({
            ...l,
            uuid: l.uuid || kedo.utils().uuidv4(),
        }))
    })
    const [combinators, setCombinators] = useState<CombinatorField[]>(() => {
        const initialCombinators =
            field?.combinators?.map((c) => ({ ...c })) || []
        return initialCombinators.map((c) => ({
            ...c,
            uuid: c.uuid || kedo.utils().uuidv4(),
        }))
    })
    const [seldata, setSeldata] = useState<string | null>(null)
    const [sellink, setSellink] = useState<string | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const [loadingDataFieldItems, setLoadingDataFieldItems] =
        useState<boolean>(false)
    const [loadingLinkFields, setLoadingLinkFields] = useState<boolean>(false)
    const [dataFields, setDataFields] = useState<DataField[]>([])
    const [linkFields, setLinkFields] = useState<LinkField[]>([])

    useEffect(() => {
        const combinator: CombinatorField = {
            uuid,
            combine,
            combinators,
            fields,
            links,
        }
        if (defdossierlink) {
            combinator.id = defdossierlink
        }

        onChange?.(index, combinator)
    }, [uuid, combine, combinators, fields, links])

    const fetchDataFields = (ddId: number) => {
        setLoadingDataFieldItems(true)
        kedo.api()
            .getCached(kedo.api().getDefDossierDefFieldEndpoint(), {
                params: { defDossier: ddId, limit: 500 },
            })
            .then((response: any) => {
                const responseData: DataField[] = response.data.results

                const systemFields: DataField[] = [
                    {
                        id: 'created_at',
                        uuid: kedo.utils().uuidv4(),
                        name: 'created_at',
                        def_field: { type: 'systemdate' },
                    },
                    {
                        id: 'modified_at',
                        uuid: kedo.utils().uuidv4(),
                        name: 'modified_at',
                        def_field: { type: 'systemdate' },
                    },
                    {
                        id: 'created_by',
                        uuid: kedo.utils().uuidv4(),
                        name: 'created_by',
                        def_field: { type: 'systemuser' },
                    },
                    {
                        id: 'modified_by',
                        uuid: kedo.utils().uuidv4(),
                        name: 'modified_by',
                        def_field: { type: 'systemuser' },
                    },
                ]
                setDataFields([...responseData, ...systemFields])
                setLoadingDataFieldItems(false)
            })
    }

    const fetchLinkFields = (ddId: number) => {
        setLoadingLinkFields(true)
        kedo.api()
            .get(kedo.api().getDefDossierLinkEndpoint(), {
                params: {
                    defDossier: ddId,
                    environment: kedo.env().getEnvironment().id,
                    limit: 500,
                },
            })
            .then((response: any) => {
                setLinkFields(response.data.results)
                setLoadingLinkFields(false)
            })
    }

    const fetchFields = () => {
        if (defdossier) {
            fetchDataFields(defdossier)
            fetchLinkFields(defdossier)
        } else if (defdossierlink) {
            kedo.api()
                .get(
                    kedo.api().getDefDossierLinkEndpoint() +
                        '/' +
                        defdossierlink
                )
                .then((response: any) => {
                    fetchDataFields(response.data.child_def_dossier_id)
                    fetchLinkFields(response.data.child_def_dossier_id)
                })
        }
    }

    useEffect(() => {
        fetchFields()
    }, [])

    const onChangeField = (fieldIndex: number, fieldValue: DataField) => {
        const updatedFields = [...fields]
        updatedFields[fieldIndex] = fieldValue
        setFields(updatedFields)
        //handleOnChange()
    }

    const onChangeCombinator = (
        combIndex: number,
        combinator: CombinatorField
    ) => {
        const updatedCombinators = [...combinators]
        updatedCombinators[combIndex] = combinator
        setCombinators(updatedCombinators)
        //handleOnChange()
    }

    const onChangeLink = (linkIndex: number, linkValue: LinkField) => {
        const updatedLinks = [...links]
        updatedLinks[linkIndex] = linkValue
        setLinks(updatedLinks)
        //handleOnChange()
    }

    const addCombinator = () => {
        const newCombinator: CombinatorField = { uuid: kedo.utils().uuidv4() }
        if (ddObject?.id) newCombinator.id = ddObject.id
        else if (linkField?.id) newCombinator.id = linkField.id
        setCombinators([...combinators, newCombinator])
        //handleOnChange()
    }

    const addLinkField = () => {
        if (!sellink) return
        const newUuid = kedo.utils().uuidv4()
        setLinks([...links, { id: parseInt(sellink), uuid: newUuid }])
        //handleOnChange()
    }

    const addDataField = () => {
        if (!seldata) return
        const newUuid = kedo.utils().uuidv4()
        let newField: DataField
        if (seldata === 'created_at' || seldata === 'modified_at') {
            newField = {
                id: seldata,
                name: seldata,
                def_field: { type: 'systemdate' },
                uuid: newUuid,
            }
        } else if (seldata === 'created_by' || seldata === 'modified_by') {
            newField = {
                id: seldata,
                name: seldata,
                def_field: { type: 'systemuser' },
                uuid: newUuid,
            }
        } else {
            newField = { id: parseInt(seldata), uuid: newUuid }
        }
        setFields([...fields, newField])
        //handleOnChange()
    }

    const onDeleteField = (uuid: string) => {
        setFields(fields.filter((f) => f.uuid !== uuid))
        //handleOnChange()
    }

    const onDeleteLink = (uuid: string) => {
        setLinks(links.filter((l) => l.uuid !== uuid))
        //handleOnChange()
    }

    const onDeleteCombinator = (uuid: string) => {
        setCombinators(combinators.filter((c) => c.uuid !== uuid))
        //handleOnChange()
    }

    const fieldTypes = [
        'text',
        'textarea',
        'autoIncrement',
        'calc',
        'email',
        'url',
        'user',
        'list',
        'date',
        'systemdate',
        'systemuser',
        'amount',
    ]

    const transparency = 0.14
    const backgrounds = [
        `rgba(179, 186, 197, ${transparency})`,
        `rgba(255, 235, 230, ${transparency})`,
        `rgba(255, 250, 230, ${transparency})`,
        `rgba(227, 252, 239, ${transparency})`,
        `rgba(222, 235, 255, ${transparency})`,
        `rgba(230, 252, 255, ${transparency})`,
        `rgba(179, 186, 197, ${transparency})`,
        `rgba(255, 235, 230, ${transparency})`,
        `rgba(255, 250, 230, ${transparency})`,
        `rgba(227, 252, 239, ${transparency})`,
        `rgba(222, 235, 255, ${transparency})`,
        `rgba(230, 252, 255, ${transparency})`,
    ]

    if (loading) return <LoadingDefault />

    return (
        <Card
            variant="outlined"
            sx={{
                mb: 2,
                borderRadius: 1,
                borderColor:
                    index % 2 === 0
                        ? brandedTokens.variables.primary
                        : brandedTokens.variables.secondary,
                backgroundColor: backgrounds[level],
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    px: 2,
                    py: 1,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: brandedTokens.variables.secondary,
                    color: brandedTokens.variables.white,
                }}
            >
                <Typography component="span" gutterBottom sx={{ pr: 2, mt: 1 }}>
                    Combinator {ddObject?.name_plural} {linkField?.name}
                </Typography>
                {(loadingDataFieldItems || loadingLinkFields) && (
                    <LoadingDefault as="span" />
                )}
                <Stack
                    direction="row"
                    spacing={1}
                    justifyContent="flex-end"
                    alignItems="center"
                >
                    <Select
                        value={combine}
                        size="small"
                        displayEmpty
                        sx={{
                            minWidth: 150,
                            backgroundColor: 'background.paper',
                        }}
                        onChange={(e) => {
                            setCombine(e.target.value as 'AND' | 'OR')
                            //handleOnChange()
                        }}
                    >
                        <MenuItem value="AND">{kedo.t('AND')}</MenuItem>
                        <MenuItem value="OR">{kedo.t('OR')}</MenuItem>
                    </Select>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <CompactButton onClick={addCombinator}>
                            <FontAwesomeIcon icon={faPlus} />
                        </CompactButton>
                        <CompactButton
                            color="secondary"
                            onClick={() => onDelete(uuid)}
                        >
                            <FontAwesomeIcon icon={faTimes} />
                        </CompactButton>
                    </Box>
                </Stack>
            </Box>
            <Box>
                <Grid container alignItems={'center'}>
                    <Grid container size={'auto'} sx={{ alignItems: 'center' }}>
                        <Typography gutterBottom sx={{ m: 0, pr: 2 }}>
                            {kedo.t('Data field')}{' '}
                            {loadingDataFieldItems && <LoadingDefault />}
                        </Typography>
                    </Grid>
                    <Grid size={'auto'} sx={{ flexGrow: 1 }}>
                        <FormControl
                            fullWidth
                            size="small"
                            sx={{ flexDirection: 'row', alignItems: 'center' }}
                        >
                            <Select
                                name="seldata"
                                value={seldata ?? ''}
                                size="small"
                                displayEmpty
                                onChange={(e) => setSeldata(e.target.value)}
                                sx={{ flexGrow: 1 }}
                                MenuProps={listSubheaderProps}
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>
                                {fieldTypes.map((ft) => [
                                    <ListSubheader key={`${ft}-subheader`}>
                                        {ft}
                                    </ListSubheader>,
                                    ...dataFields
                                        .filter(
                                            (df) => df.def_field?.type === ft
                                        )
                                        .map((df) => (
                                            <MenuItem key={df.id} value={df.id}>
                                                {df.name}
                                            </MenuItem>
                                        )),
                                ])}
                            </Select>
                            <CompactButton onClick={addDataField}>
                                <FontAwesomeIcon icon={faPlus} />
                            </CompactButton>
                        </FormControl>
                    </Grid>
                </Grid>
                <br />
                <Grid container alignItems={'center'}>
                    <Grid container size={'auto'} sx={{ alignItems: 'center' }}>
                        <Typography gutterBottom sx={{ m: 0, pr: 2 }}>
                            {kedo.t('Link field')}{' '}
                            {loadingLinkFields && <LoadingDefault />}
                        </Typography>
                    </Grid>
                    <Grid size={'auto'} sx={{ flexGrow: 1 }}>
                        <FormControl
                            fullWidth
                            size="small"
                            sx={{
                                flexDirection: 'row',
                                alignItems: 'center',
                                mb: 1,
                            }}
                        >
                            <Select
                                name="sellink"
                                value={sellink ?? ''}
                                size="small"
                                displayEmpty
                                onChange={(e) => setSellink(e.target.value)}
                                sx={{ flexGrow: 1 }}
                            >
                                <MenuItem value="">
                                    {kedo.t('Choose an option')}
                                </MenuItem>
                                {linkFields.map((lf) => (
                                    <MenuItem key={lf.id} value={lf.id}>
                                        {lf.name} - {lf.id}
                                    </MenuItem>
                                ))}
                            </Select>
                            <CompactButton onClick={addLinkField}>
                                <FontAwesomeIcon icon={faPlus} />
                            </CompactButton>
                        </FormControl>
                    </Grid>
                </Grid>
                <br />

                {fields.map((f, i) => (
                    <QueryBuilderField
                        key={f.uuid}
                        index={i}
                        field={f}
                        onDelete={onDeleteField}
                        onChange={onChangeField}
                        kedo={kedo}
                        dataField={dataFields.find((df) => df.id === f.id)}
                    />
                ))}

                {links.map((l, i) => (
                    <QueryBuilderLink
                        key={l.uuid}
                        field={l}
                        level={level + 1}
                        kedo={kedo}
                        defdossierlink={l.id}
                        onDelete={onDeleteLink}
                        index={i}
                        onChange={onChangeLink}
                        linkField={linkFields.find((lf) => lf.id === l.id)}
                    />
                ))}

                {combinators.map((c, i) => (
                    <QueryBuilderCombinator
                        key={c.uuid}
                        field={c}
                        level={level + 1}
                        defdossier={defdossier}
                        kedo={kedo}
                        onDelete={onDeleteCombinator}
                        defdossierlink={c.id}
                        index={i}
                        onChange={onChangeCombinator}
                        linkField={linkFields.find((lf) => lf.id === c.id)}
                    />
                ))}
            </Box>
        </Card>
    )
}

export default QueryBuilderCombinator
